import React, { useMemo, useState, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  getAccessPolicy, isRouteAllowed,
  requiresKeyGate, validateKeyGate, isKeyGateVerified, setKeyGateVerified
} from '../config/accessControl';
import { pathFor } from '../config/routeMap';

const RequireAuth = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function useUserEmail() {
  const { instance } = useMsal();
  return useMemo(() => {
    let account = instance.getActiveAccount?.();
    if (!account) {
      const all = instance.getAllAccounts?.() || [];
      if (all.length) account = all[0];
    }
    return account?.username || account?.idTokenClaims?.preferred_username || account?.idTokenClaims?.email || '';
  }, [instance]);
}

export const RequireAccess = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const email = useUserEmail();
  const location = useLocation();

  const policy = useMemo(() => getAccessPolicy(email), [email]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!isRouteAllowed(location.pathname, policy, pathFor)) {
    return <Navigate to="/trust-tech-studio" replace />;
  }

  return children;
};

export const RequireKeyGate = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const email = useUserEmail();
  const location = useLocation();

  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(isKeyGateVerified);

  const needsGate = useMemo(
    () => requiresKeyGate(email, location.pathname),
    [email, location.pathname]
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (validateKeyGate(keyInput.trim())) {
      setKeyGateVerified();
      setVerified(true);
      setError('');
    } else {
      setError('Invalid access key. Please try again.');
    }
  }, [keyInput]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (needsGate && !verified) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: 'linear-gradient(165deg, #0c0a15 0%, #1a1035 50%, #0c0a15 100%)' }}>
        <div className="card shadow-lg border-0" style={{ maxWidth: '480px', width: '100%', background: '#13111d', border: '1px solid rgba(139,92,246,0.2)' }}>
          <div className="card-body p-5">
            <div className="text-center mb-4">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{ width: '56px', height: '56px', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
                <i className="bi bi-shield-lock-fill" style={{ fontSize: '1.5rem', color: '#a78bfa' }}></i>
              </div>
              <h4 className="fw-bold mb-1" style={{ color: '#e2e8f0' }}>Sovereign Access Required</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                Enter your Registry Settlement ID (RSID) to continue.
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  RSID Access Key
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={keyInput}
                  onChange={(e) => { setKeyInput(e.target.value); setError(''); }}
                  placeholder="RSID..."
                  autoFocus
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${error ? '#ef4444' : 'rgba(139,92,246,0.25)'}`,
                    color: '#e2e8f0',
                    fontSize: '0.9rem',
                    padding: '0.75rem 1rem',
                  }}
                />
                {error && (
                  <div className="mt-2" style={{ fontSize: '0.8rem', color: '#ef4444' }}>
                    <i className="bi bi-exclamation-circle me-1"></i>{error}
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="btn w-100 fw-semibold"
                style={{
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: 'white',
                  padding: '0.75rem',
                  border: 'none',
                  fontSize: '0.9rem',
                }}
              >
                Verify Access
              </button>
            </form>
            <div className="text-center mt-4">
              <p style={{ fontSize: '0.72rem', color: '#64748b' }}>
                <i className="bi bi-lock-fill me-1"></i>
                Protected by KRO Sovereign Registry Protocol
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default RequireAuth;
